import { validateExerciseSet } from "../../domain/exercises/validate.ts";
import { findMeaningById } from "../../domain/vocabulary/grouping.ts";
import type { VocabularySnapshot } from "../../domain/vocabulary/types.ts";
import type { MeaningPackage, SelectedSense } from "../../contracts/ai/schemas.ts";
import type { ExerciseValidationIssue } from "../../domain/exercises/validate.ts";

export function validateMeaningPackage(
  pack: MeaningPackage,
  selected: SelectedSense,
  snapshot: VocabularySnapshot,
): ExerciseValidationIssue[] {
  const issues: ExerciseValidationIssue[] = [];
  if (pack.itemId !== selected.itemId) {
    issues.push({ code: "item_mismatch", message: "Generated package does not match the selected meaning." });
  }
  if (pack.type !== selected.type) {
    issues.push({
      code: "type_mismatch",
      message: "Generated type does not match the locked sense. Reselect rather than changing meaning silently.",
    });
  }
  const seenTargets = new Set<string>();
  for (const relation of pack.relations) {
    if (relation.targetMeaningId === selected.itemId) {
      issues.push({ code: "self_relation", message: "A meaning cannot relate to itself." });
    }
    if (seenTargets.has(relation.targetMeaningId)) {
      issues.push({ code: "duplicate_relation", message: "Duplicate related-meaning pair." });
    }
    seenTargets.add(relation.targetMeaningId);
    if (!findMeaningById(snapshot, relation.targetMeaningId)) {
      issues.push({
        code: "invalid_relation_id",
        message: `Related meaning ${relation.targetMeaningId} is not in the saved vocabulary.`,
      });
    }
  }
  const hasUsefulRelations = pack.relations.length > 0;
  issues.push(
    ...validateExerciseSet(
      { meaningId: selected.itemId, exercises: pack.exercises },
      snapshot,
      hasUsefulRelations,
    ),
  );
  return issues;
}

export function buildReviewNote(
  selected: SelectedSense,
  pack: MeaningPackage,
): string | null {
  const notes: string[] = [];
  if (selected.sourceType && selected.sourceType !== pack.type) {
    notes.push(`Type was adjusted from ${selected.sourceType} to ${pack.type}.`);
  }
  if (selected.sourceDefinition && selected.sourceDefinition.trim() !== pack.definition.trim()) {
    notes.push("The definition was rewritten into a shorter learner-friendly form.");
  }
  if (selected.sourceHint && pack.russianHint && selected.sourceHint.trim() !== pack.russianHint.trim()) {
    notes.push("The Russian hint was corrected.");
  }
  if (pack.reviewNote) {
    notes.push(pack.reviewNote);
  }
  return notes.length ? notes.join(" ") : null;
}
